import { FC, SyntheticEvent, useCallback, useMemo } from "react";
import { Typography } from "theme";
import { ArrowDown } from "theme/icons";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid2,
  Radio,
  Tooltip,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  AvailableSlot,
  MappedAvailableSlots,
} from "../../../../api/hooks/useAvailableSlots/type.ts";
import {
  AFTER_HOURS_CONSTANT,
  MORNING_HOURS_CONSTANT,
} from "../../../../api/hooks/useAvailableSlots/useAvailableSlots.ts";
import { SelectionRadioGroupProps } from "../../HallsForm/components/SelectionRadioGroup.tsx";

export type TimeSelectionRadioGroupProps = Omit<SelectionRadioGroupProps, "options"> & {
  options: AvailableSlot[];
  shouldShowPrice?: boolean;
};

const LabelWrapper: FC<{
  option: AvailableSlot;
  checked: boolean;
  disabled: boolean;
  shouldShowPrice?: boolean;
}> = ({ option, checked, disabled, shouldShowPrice }) => {
  return (
    <Grid2 container flexDirection="column" alignItems="center">
      <Typography variant="body1" color={disabled ? "primary" : checked ? "secondary" : "primary"}>
        {option.slotValue}
      </Typography>
      {shouldShowPrice && (
        <Typography
          lineHeight={1}
          variant="body2"
          color={disabled ? "primary" : checked ? "secondary" : "primary"}
        >
          {option.price * 2}₴
        </Typography>
      )}
    </Grid2>
  );
};

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, checked, disabled }) => {
  return {
    "&.MuiFormControlLabel-root": {
      margin: 0,
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(0.75, 2.25),
      boxSizing: "border-box",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: theme.palette.success.main,
      borderRadius: 55,

      ...(checked && {
        "&.MuiFormControlLabel-root": {
          backgroundColor: theme.palette.success.main,
        },
      }),

      ...(disabled && {
        "&.MuiFormControlLabel-root": {
          backgroundColor: theme.palette.background.inactive,
          borderColor: theme.palette.background.inactive,
        },
      }),

      "& .MuiButtonBase-root.PrivateSwitchBase-root": {
        display: "none",
      },
    },
  };
});

const StyledAccordion = styled(Accordion)(({ theme }) => {
  return {
    "&.MuiAccordion-root": {
      backgroundColor: "transparent",
      boxShadow: "none",
      margin: theme.spacing(1.25, 0),

      "&.Mui-expanded": {
        marginTop: theme.spacing(1.25),
      },

      "&::before": {
        display: "none",
      },

      "& .MuiAccordionSummary-root, & .MuiAccordionDetails-root": {
        padding: 0,
      },

      "& .MuiAccordionSummary-root": {
        minHeight: 0,
      },

      "& .MuiCollapse-root": {
        marginTop: theme.spacing(1.25),
      },

      "& .MuiAccordionSummary-root .MuiAccordionSummary-content": {
        margin: 0,

        "& .MuiTypography-root": {
          margin: 0,
        },
      },
    },
  };
});

const StyledWrap = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
}));

const TimeSelectionRadioGroup: FC<TimeSelectionRadioGroupProps> = ({
  onChange,
  options,
  name,
  value,
  shouldShowPrice,
}) => {
  const theme = useTheme();
  const handleChange = useCallback(
    (e: SyntheticEvent) => {
      const changedValue = (e.currentTarget as HTMLInputElement).value;

      onChange(changedValue);
    },
    [onChange],
  );

  const mappedOptions = useMemo<MappedAvailableSlots>(() => {
    if (shouldShowPrice) {
      return {
        morningSlots: {
          label: "Нічні години",
          slots: options,
        },
        afternoonSlots: {
          label: "",
          slots: [],
        },
        eveningSlots: {
          label: "",
          slots: [],
        },
      };
    }

    const mSlots = options.filter((slot) => slot.slotValue < MORNING_HOURS_CONSTANT);
    const aSlots = options.filter(
      (slot) => slot.slotValue >= MORNING_HOURS_CONSTANT && slot.slotValue < AFTER_HOURS_CONSTANT,
    );
    const eSlots = options.filter((slot) => slot.slotValue >= AFTER_HOURS_CONSTANT);

    return {
      morningSlots: {
        label: "Ранок",
        slots: mSlots,
      },
      afternoonSlots: {
        label: "День",
        slots: aSlots,
      },
      eveningSlots: {
        label: "Вечір",
        slots: eSlots,
      },
    };
  }, [options, shouldShowPrice]);

  return (
    <FormControl fullWidth>
      <FormGroup>
        {mappedOptions.morningSlots.slots.length > 0 && (
          <StyledAccordion defaultExpanded>
            <AccordionSummary expandIcon={<ArrowDown />}>
              <Typography mb={theme.spacing(1.25)} variant="body1" textTransform="uppercase">
                {mappedOptions.morningSlots.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <StyledWrap gap={theme.spacing(0.5)} flexWrap="wrap">
                {mappedOptions.morningSlots.slots.map((slot) => (
                  <StyledFormControlLabel
                    key={slot.slotDateTime}
                    name={name}
                    value={slot.slotDateTime}
                    checked={value === slot.slotDateTime}
                    control={<Radio />}
                    disabled={slot?.disabled}
                    label={
                      <LabelWrapper
                        shouldShowPrice={shouldShowPrice}
                        disabled={!!slot?.disabled}
                        option={slot}
                        checked={value === slot.slotDateTime}
                      />
                    }
                    onChange={handleChange}
                  />
                ))}
              </StyledWrap>
            </AccordionDetails>
          </StyledAccordion>
        )}
        {mappedOptions.afternoonSlots.slots.length > 0 && (
          <StyledAccordion defaultExpanded>
            <AccordionSummary expandIcon={<ArrowDown />}>
              <Typography mb={theme.spacing(1.25)} variant="body1" textTransform="uppercase">
                {mappedOptions.afternoonSlots.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <StyledWrap gap={theme.spacing(0.5)} flexWrap="wrap">
                {mappedOptions.afternoonSlots.slots.map((slot) => (
                  <StyledFormControlLabel
                    key={slot.slotDateTime}
                    name={name}
                    value={slot.slotDateTime}
                    checked={value === slot.slotDateTime}
                    control={<Radio />}
                    disabled={slot?.disabled}
                    label={
                      <LabelWrapper
                        shouldShowPrice={shouldShowPrice}
                        disabled={!!slot?.disabled}
                        option={slot}
                        checked={value === slot.slotDateTime}
                      />
                    }
                    onChange={handleChange}
                  />
                ))}
              </StyledWrap>
            </AccordionDetails>
          </StyledAccordion>
        )}
        {mappedOptions.eveningSlots.slots.length > 0 && (
          <StyledAccordion defaultExpanded>
            <AccordionSummary expandIcon={<ArrowDown />}>
              <Typography mb={theme.spacing(1.25)} variant="body1" textTransform="uppercase">
                {mappedOptions.eveningSlots.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <StyledWrap gap={theme.spacing(0.5)} flexWrap="wrap">
                {mappedOptions.eveningSlots.slots.map((slot) =>
                  slot?.disabled ? (
                    <Tooltip
                      key={slot.slotDateTime}
                      placement="top"
                      arrow
                      disableFocusListener
                      title="Недоступний час"
                    >
                      <StyledFormControlLabel
                        name={name}
                        value={slot.slotDateTime}
                        checked={value === slot.slotDateTime}
                        control={<Radio />}
                        disabled={slot?.disabled}
                        label={
                          <LabelWrapper
                            shouldShowPrice={shouldShowPrice}
                            disabled={!!slot?.disabled}
                            option={slot}
                            checked={value === slot.slotDateTime}
                          />
                        }
                        onChange={handleChange}
                      />
                    </Tooltip>
                  ) : (
                    <StyledFormControlLabel
                      key={slot.slotDateTime}
                      name={name}
                      value={slot.slotDateTime}
                      checked={value === slot.slotDateTime}
                      control={<Radio />}
                      disabled={slot?.disabled}
                      label={
                        <LabelWrapper
                          shouldShowPrice={shouldShowPrice}
                          disabled={!!slot?.disabled}
                          option={slot}
                          checked={value === slot.slotDateTime}
                        />
                      }
                      onChange={handleChange}
                    />
                  ),
                )}
              </StyledWrap>
            </AccordionDetails>
          </StyledAccordion>
        )}
      </FormGroup>
    </FormControl>
  );
};

export default TimeSelectionRadioGroup;
